import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TrainedModelProviderInstance } from "../@shared/services/TrainedModelProvider";
import { NotificationModel } from "../reducers/main/mainModel";
import { onThrowAlert } from "../sagas/dataSaga/dataSagaActions";
import { MetricsResult } from "../@shared/Models/Models";
import { getIsShowCalculateMetricsModal, setIsShowCalculateMetricsModal } from "../reducers/trainedModel/trainedModelReducer";
import { getOpenedModel } from "../reducers/data/dataReducer";


export type UseCalculateMetricsResult = {
    onCalculate: () => void;
    onDataUpdate: (newData: string) => void;
    onSetSchemaOrdering: (newOrdering: string) => void;
    isProcessing: boolean;
    metrics: MetricsResult | null;
    data: string;
    schemaOrdering: string;
    isOpen: boolean;
    handleOpen: () => void;
    handleClose: () => void;
}

export const useCalculateMetrics = () => {
    const dispatch = useDispatch();
    const [isProcessing, setIsProcessing] = useState(false);
    const [metrics, setMetrics] = useState<MetricsResult | null>(null);
    const [data, setData] = useState<string>("");
    const [schemaOrdering, setSchemaOrdering] = useState<string>("");
    const isOpen = useSelector(getIsShowCalculateMetricsModal);

    const model = useSelector(getOpenedModel);

    const handleOpen = useCallback(() => {
        setMetrics(null);
        dispatch(setIsShowCalculateMetricsModal(true));
    }, [dispatch]);

    const handleClose = useCallback(() => {
        dispatch(setIsShowCalculateMetricsModal(false));
        setMetrics(null);
    }, [dispatch]);

    const onCalculate = useCallback(async () => {
        if (!model) {
            dispatch(onThrowAlert({
                message: "No model is opened for metrics calculation.",
                lifeTime: 10000
            } as NotificationModel));
            return;
        }
        try {
            setIsProcessing(true);
            const result = await TrainedModelProviderInstance.calculateMetrics(model?.id, { 
                data: JSON.parse(data),
                schemaOrdering: schemaOrdering.split(",").map(x => x.trim()) 
            });
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                setMetrics(result.data);
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }
    }, [data, dispatch, model, schemaOrdering])

    const onDataUpdate = useCallback((newData: string) => {
        setData(newData);
    }, []);

    const onSetSchemaOrdering = useCallback((newOrdering: string) => {
        setSchemaOrdering(newOrdering);
    }, []);

    return {
        onCalculate,
        onDataUpdate,
        onSetSchemaOrdering,
        isProcessing,
        metrics,
        data,
        schemaOrdering,
        isOpen,
        handleOpen,
        handleClose
    }
}